import type { TFunction } from 'i18next';
import { formatClock, formatDuration } from '@/features/trips/format';
import { useAuth } from '@/features/auth/useAuth';
import { useTenantWritable } from '@/features/auth/useTenantWritable';
import { useAuthoriseVehicle } from './useWorkingHours';
import type { AfterHoursAlert } from './types';

/**
 * One after-hours-use row (slice 37) — the FIFTH item shape of the alerts feed,
 * beside the geofence, overspeed, maintenance and tracker-health rows. It reports
 * a vehicle used outside the tenant's working hours: which vehicle, when it
 * started, and whether the episode is still open. Like overspeed (and unlike
 * maintenance / tracker-health) it HAS an observed location — the anchor fix — so
 * selecting the row centres the map on it, and marking-read on click is the same
 * `onSelect` path every positioned row uses.
 *
 * Severity is always `warning` here (the wire literal): an after-hours use is
 * always attention-worthy, so there is no non-alerting direction to mute, exactly
 * like an overspeed episode.
 *
 * 🔴 AN OPEN EPISODE (endedAt null) OFFERS "Authorise this trip" — owner/manager
 * only, absent for a viewer (absence over disabled). It POSTs authorise for THIS
 * vehicle, which suppresses a further after-hours alert until the trip ends; it
 * never rewrites the alert already fired (the server enforces that — the UI just
 * offers the action). The authorise control is a SIBLING button, never nested
 * inside the row's select button (a button inside a button is invalid), so a click
 * on it does not also fire the row selection.
 */

export function AfterHoursRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: AfterHoursAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: AfterHoursAlert) => void;
  t: TFunction;
}) {
  const { state } = useAuth();
  const role = state.status === 'authenticated' ? state.user.role : 'viewer';
  // Slice 11b — authorise is a write, refused (402) while suspended; hide it.
  const writable = useTenantWritable();
  const canAuthorise = (role === 'owner' || role === 'manager') && writable;

  const authorise = useAuthoriseVehicle(alert.vehicleId);

  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const isOpen = alert.endedAt == null;
  const durationSec = !isOpen
    ? Math.max(0, Math.round((Date.parse(alert.endedAt!) - Date.parse(alert.startedAt)) / 1000))
    : null;
  const windowText =
    durationSec != null
      ? t('alerts.afterHours.window', { duration: formatDuration(t, durationSec) })
      : t('alerts.afterHours.ongoing');

  const titleClass = !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : 'bg-raad-warn';

  return (
    <li>
      <div
        className={[
          'rounded-xl border transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={() => onSelect(alert)}
          aria-pressed={selected}
          data-testid="after-hours-row"
          data-alert-id={alert.id}
          data-open={isOpen ? 'true' : 'false'}
          className="w-full px-3.5 py-3 text-left"
        >
          <div className="flex items-start gap-2.5">
            <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className={['truncate text-sm', titleClass].join(' ')}>
                  {t('alerts.afterHours.headline')}
                </span>
                <span
                  data-testid="after-hours-tag"
                  className="flex-none rounded-md bg-raad-warn/18 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-warn"
                >
                  {t('alerts.afterHours.severity')}
                </span>
                {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
              </div>

              {plate ? (
                <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
              ) : null}

              <div className="mt-1 text-[12px] text-raad-dim">
                {t('alerts.observedAt', { time: observedTime })} · {windowText}
              </div>
            </div>
          </div>
        </button>

        {/* AUTHORISE — only for an OPEN episode, and only for a writer. A sibling
            of the select button (never nested), so its click does not also select
            the row. */}
        {isOpen && canAuthorise ? (
          <div className="flex items-center gap-2.5 border-t border-raad-line-7 px-3.5 py-2">
            {authorise.isSuccess ? (
              <span className="text-[12px] font-semibold text-raad-accent" role="status">
                {t('alerts.afterHours.authorised')}
              </span>
            ) : (
              <button
                type="button"
                data-testid="after-hours-authorise"
                onClick={() => authorise.mutate(true)}
                disabled={authorise.isPending}
                className="min-h-9 rounded-lg border border-raad-line-10 px-3 text-[12px] font-semibold text-raad-text transition-colors hover:border-raad-line-20 disabled:opacity-40"
              >
                {authorise.isPending
                  ? t('alerts.afterHours.authorising')
                  : t('alerts.afterHours.authorise')}
              </button>
            )}
            {authorise.isError ? (
              <span className="text-[12px] text-raad-error" role="alert">
                {t('alerts.afterHours.authoriseFailed')}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
